// src/components/toolkit/QuestionDeck.jsx — goes with Module 4.
//
// The module's five question types, dealt three at a time. The questions are
// the module's own examples; the deck just decides which ones you take to the
// table tonight.
import { useMemo, useState } from 'react';
import { ArrowLeft, Check, MessageCircle, Shuffle } from 'lucide-react';
import { QUESTION_TYPES } from '../../data/toolkitData';
import { loadTool, saveTool } from './toolStorage';

const TOOL_ID = 'question-deck';
const card = 'rounded-xl border border-thread-border bg-white/5 p-5';

const DECK = QUESTION_TYPES.flatMap((type) =>
  type.examples.map((question) => ({ question, type: type.name, purpose: type.purpose })),
);

const dealThree = (pool) => {
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, 3);
};

const QuestionDeck = ({ onBack, onComplete }) => {
  const [state, setState] = useState(() => loadTool(TOOL_ID, { kept: [], done: false }));
  const [filter, setFilter] = useState('all');
  const [hand, setHand] = useState([]);

  const pool = useMemo(() => (filter === 'all' ? DECK : DECK.filter((c) => c.type === filter)), [filter]);

  const update = (next) => {
    const merged = { ...state, ...next };
    setState(merged);
    saveTool(TOOL_ID, merged);
  };

  const keep = () => {
    update({ kept: hand, done: true });
    onComplete?.({ questions: hand.map((c) => c.question) });
  };

  return (
    <div className="min-h-screen bg-thread-darker text-white pt-36 pb-20">
      <div className="container mx-auto px-4 max-w-3xl">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 min-h-[44px] text-thread-sand hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to the toolkit
        </button>

        <h1 className="text-3xl md:text-4xl font-bold font-display mb-3">Dinner question deck</h1>
        <p className="text-slate-300 mb-6">
          Three questions, dealt at random. Ask one and let it run: the good stuff usually turns up in the answer after
          the answer.
        </p>

        <div className="flex flex-wrap gap-2 mb-6" role="group" aria-label="Kind of question">
          {['all', ...QUESTION_TYPES.map((t) => t.name)].map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setFilter(name)}
              aria-pressed={filter === name}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                filter === name
                  ? 'border-thread-glow bg-thread-ember/20 text-white'
                  : 'border-white/15 bg-white/5 text-slate-300 hover:border-thread-glow/60'
              }`}
            >
              {name === 'all' ? 'Any kind' : name.replace(' Questions', '')}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setHand(dealThree(pool))}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-thread-ember hover:bg-thread-rust text-white font-semibold transition-colors mb-6"
        >
          <Shuffle className="w-4 h-4" aria-hidden="true" /> {hand.length ? 'Deal again' : 'Deal three'}
        </button>

        {hand.length > 0 && (
          <section aria-label="Your three questions" className="space-y-3 mb-6">
            {hand.map((cardItem) => (
              <div key={cardItem.question} className={card}>
                <p className="text-lg text-white">{cardItem.question}</p>
                <p className="text-sm text-thread-sand mt-2">
                  {cardItem.type.replace(' Questions', '')} · {cardItem.purpose.toLowerCase()}
                </p>
              </div>
            ))}
            <button
              type="button"
              onClick={keep}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/25 hover:bg-white/10 text-white font-semibold transition-colors"
            >
              <Check className="w-4 h-4" aria-hidden="true" /> Keep these for tonight
            </button>
          </section>
        )}

        {state.kept?.length > 0 && (
          <section aria-label="Saved questions" className={card}>
            <h2 className="flex items-center gap-2 font-semibold text-white mb-2">
              <MessageCircle className="w-5 h-5 text-thread-glow" aria-hidden="true" /> Saved for tonight
            </h2>
            <ul className="list-disc list-inside text-slate-300 space-y-1">
              {state.kept.map((item) => (
                <li key={item.question}>{item.question}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default QuestionDeck;
